import React from 'react'
import Layout from '../../components/layout/layout'
import { Box, Columns, Container, Element, Heading, Section, Button } from 'react-bulma-components'
import PageTitle from '../../components/pages/page-title'
import PayrollFilters from '../../components/payroll/filters'
import { WorkerCounter } from '../../api/worker-counters'
import useStore from '../../store'
import { Organization } from '../../api/organizations'
import WorkerCounterModal from '../../components/worker-counters/worker-counter-modal'
import { useModal } from '../../components/modal'
import useFilters from '../../hooks/filters'
import { FiltersResourcesEnum } from '../../store/filters'
import Avatar from '../../components/avatar/avatar'
import WorkersList from '../../components/payroll/workers-list'
import PayrollStats from '../../components/payroll/payroll-stats'
import { PayrollWeeksTable } from '../../components/payroll/weeks-table'
import PayrollEventsSummary from '../../components/payroll/payroll-events-summary'
import { usePayrollWorkers, useWorkerWeeksSummary } from '../../hooks/payroll'
import { getDocIdAsString } from '../../utils/documents'
import { useCreateTwoPartiesMutation } from '../../queries/period-summaries'
import ClosePayrollWeekModal from '../../components/payroll/modals/close-payroll-week-modal'
import PayrollWorkerPeriodDetails from '../../components/payroll/worker-period-details'
import { useElementSize } from '../../hooks/use-element-size'

const PayrollDetails: React.FC = () => {
  // Measure right column height to sync left column container height
  const [rightColRef, rightColSize] = useElementSize<HTMLDivElement>()
  const { filters, setFilters } = useFilters(FiltersResourcesEnum.payroll)
  const [selectedWorkerCounter, setSelectedWorkerCounter] = React.useState<WorkerCounter | null>(
    null,
  )
  const currentOrganization = useStore(state => state.session.currentOrganization) as Organization

  // Use currentWorkerId from filters instead of local state
  const currentWorkerId = filters.currentWorkerId
  const setCurrentWorkerId = React.useCallback(
    (workerId: string | undefined) => {
      if (workerId === filters.currentWorkerId) return
      setFilters(FiltersResourcesEnum.payroll, {
        ...filters,
        currentWorkerId: workerId,
      })
    },
    [filters, setFilters],
  )

  // Utilisation du hook pour récupérer les workers avec toutes leurs données agrégées
  const {
    payrollWorkers,
    allWorkPeriodEvents,
    weeks,
    weeksSummaryStatus,
    isLoading,
    hasPrev,
    hasNext,
    goPrev,
    goNext,
    currentWorker,
    resolvedCurrentWorkerId,
  } = usePayrollWorkers(currentOrganization, filters, currentWorkerId)

  // Full weeks summary for selected worker (separate hook)
  const currentWorkerWithWeeksSummary = useWorkerWeeksSummary(
    currentOrganization,
    filters,
    currentWorkerId,
  )

  /**
   * Go to previous/next worker using hook-provided navigation ids
   */
  const onPrev = React.useCallback(() => {
    const prevId = goPrev()
    if (prevId) setCurrentWorkerId(prevId)
  }, [goPrev])

  const onNext = React.useCallback(() => {
    const nextId = goNext()
    if (nextId) setCurrentWorkerId(nextId)
  }, [goNext])

  /**
   * Ensure current selection stays valid when the list changes
   */
  React.useEffect(() => {
    if (resolvedCurrentWorkerId) {
      setCurrentWorkerId(resolvedCurrentWorkerId)
    }
  }, [resolvedCurrentWorkerId])

  const closeWeekModal = useModal()
  const [currentWeekData, setCurrentWeekData] = React.useState<{
    weekNumber: number
    year: number
  } | null>(null)

  const createTwoPartiesMutation = useCreateTwoPartiesMutation()
  const workerCounterModal = useModal()

  // Réinitialiser le worker counter sélectionné quand la modale se ferme
  React.useEffect(() => {
    if (!workerCounterModal.isDisplayed) {
      setSelectedWorkerCounter(null)
    }
  }, [workerCounterModal.isDisplayed])

  return (
    <Layout>
      <Section>
        <Container max={true} breakpoint={'fluid'}>
          <PageTitle
            title="Données pour paie"
            subTitle={
              <>
                Analysez et clôturez les heures travaillées de vos salariés sur la période
                sélectionnée.
              </>
            }
          />
          <Box mt={4}>
            <PayrollFilters />
          </Box>
          <Box>
            <Columns>
              <Columns.Column size={2} style={{ borderRight: '1px solid #e0e0e0', padding: 0 }}>
                {/* Sticky container to keep the list within viewport and enable internal scrolling */}
                <Element
                  style={{
                    position: 'sticky',
                    top: 0,
                    // Match right column measured height; fallback to viewport height
                    maxHeight: rightColSize.height ? `${rightColSize.height + 34}px` : '100vh',
                    overflowY: 'auto',
                  }}
                >
                  {/* Statistiques des périodes */}
                  <Element p={2} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <PayrollStats
                      weeksSummaryStatus={weeksSummaryStatus}
                      totalWorkers={payrollWorkers.length}
                    />
                  </Element>
                  <Element data-test="workers-list">
                    <WorkersList
                      workers={payrollWorkers}
                      weeksSummaryStatus={weeksSummaryStatus}
                      onWorkerSelect={worker => {
                        setCurrentWorkerId(getDocIdAsString(worker))
                      }}
                      activeWorkerId={currentWorkerId}
                      isLoading={isLoading}
                    />
                  </Element>
                </Element>
              </Columns.Column>
              <Columns.Column size={10} pt={1}>
                {/* Right column measurement container */}
                <div ref={rightColRef as any} style={{ minHeight: '60vh' }}>
                  {currentWorkerWithWeeksSummary &&
                    filters?.start &&
                    filters?.end &&
                    allWorkPeriodEvents && (
                      <Element mb={2}>
                        <Heading size={4} mb={6}>
                          <Element
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Element display="flex" alignItems="center">
                              <Avatar user={currentWorkerWithWeeksSummary} />
                            </Element>
                            {/* Navigation buttons to move to previous/next worker */}
                            <Element>
                              <Button.Group>
                                <Button
                                  size="small"
                                  color="light"
                                  disabled={!hasPrev}
                                  onClick={onPrev}
                                >
                                  ← Précédent
                                </Button>
                                <Button
                                  size="small"
                                  color="light"
                                  disabled={!hasNext}
                                  onClick={onNext}
                                >
                                  Suivant →
                                </Button>
                              </Button.Group>
                            </Element>
                          </Element>
                          <Element textColor="grey" textSize={6}>
                            <PayrollWorkerPeriodDetails
                              workerCounters={currentWorkerWithWeeksSummary?.workerCounters}
                              onWorkerCounterClick={(counter: WorkerCounter) => {
                                setSelectedWorkerCounter(counter)
                                workerCounterModal.setIsDisplayed(true)
                              }}
                            />
                          </Element>
                        </Heading>
                        <PayrollWeeksTable
                          worker={currentWorkerWithWeeksSummary}
                          weeks={weeks || []}
                          events={allWorkPeriodEvents}
                          onCloseWeek={(weekNumber, year) => {
                            setCurrentWeekData({ weekNumber, year })
                            closeWeekModal.setIsDisplayed(true)
                          }}
                        />
                        <PayrollEventsSummary
                          worker={currentWorkerWithWeeksSummary}
                          weeks={weeks || []}
                          events={allWorkPeriodEvents}
                        />
                      </Element>
                    )}
                </div>
              </Columns.Column>
            </Columns>
          </Box>
        </Container>
      </Section>

      {/* Modal pour les détails du worker counter */}
      <WorkerCounterModal
        workerCounter={selectedWorkerCounter || undefined}
        isDisplayed={workerCounterModal.isDisplayed}
        setIsDisplayed={workerCounterModal.setIsDisplayed}
      />
      {currentWeekData && currentWorker && (
        <ClosePayrollWeekModal
          show={closeWeekModal.isDisplayed}
          hide={() => closeWeekModal.setIsDisplayed(false)}
          weekNumber={currentWeekData.weekNumber}
          year={currentWeekData.year}
          worker={currentWorker}
          onSubmit={createTwoPartiesMutation.mutate}
        />
      )}
    </Layout>
  )
}

export default PayrollDetails
